import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Text,
  TextInput,
  Alert,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { MeetingsService } from '@/services/meetingsService';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import SecondaryButton from '@/components/SecondaryButton';
import { SelectableChip } from '@/components/SelectableChip';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { coerceMeetingData, parseWeekdays } from '@/utils/meetingLocale';
import { supabase } from '@/lib/supabase';
import { MeetingGroup } from '@/types/groups';

export default function AddMeetingScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const { userProfile } = useAuth();
  const { editMeetingId } = useLocalSearchParams<{ editMeetingId?: string }>();
  
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<MeetingGroup[]>([]);
  const [meeting, setMeeting] = useState({
    meeting_name: '',
    meeting_category: [] as string[],
    meeting_link: '',
    meeting_time: '',
    meeting_weekday: [] as string[],
    meeting_place: [] as string[],
    meeting_period: 'morning',
    meeting_region: '',
    meeting_password: '',
    description: '',
    header_image_url: '',
    group_id: null as string | null,
  });

  const isEditMode = !!editMeetingId;
  const isAdmin = userProfile?.is_admin || false;

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('meeting_groups')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) {
        console.error('Error loading groups:', error);
        return;
      }
      
      setGroups(data || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  useEffect(() => {
    if (isEditMode && editMeetingId) {
      loadMeeting(editMeetingId);
    }
  }, [editMeetingId]);

  const loadMeeting = async (id: string) => {
    try {
      setLoading(true);
      const m = await MeetingsService.getMeetingById(id);
      
      if (m) {
        // Parse weekdays using parseWeekdays to handle both ';' and ',' delimiters
        // Then coerce legacy Portuguese values to normalized English keys
        const normalizedData = coerceMeetingData({
          ...m,
          meeting_weekday: parseWeekdays(m.meeting_weekday)
        });
        
        setMeeting({
          meeting_name: normalizedData.meeting_name,
          meeting_category: [normalizedData.meeting_category],
          meeting_link: normalizedData.meeting_link,
          meeting_time: normalizedData.meeting_time,
          meeting_weekday: normalizedData.meeting_weekday,
          meeting_place: [normalizedData.meeting_place],
          meeting_period: normalizedData.meeting_period,
          meeting_region: normalizedData.meeting_region || '',
          meeting_password: normalizedData.meeting_password || '',
          description: normalizedData.description || '',
          header_image_url: normalizedData.header_image_url || '',
          group_id: normalizedData.group_id || null,
        });
      } else {
        Alert.alert(t('common.alert.errorTitle'), t('meetings.alerts.notFound'));
        router.back();
      }
    } catch (error) {
      console.error('Error loading meeting:', error);
      Alert.alert(t('common.alert.errorTitle'), t('meetings.alerts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleWeekdayToggle = (weekday: string) => {
    setMeeting(prev => {
      const currentWeekdays = prev.meeting_weekday;
      if (currentWeekdays.includes(weekday)) {
        return { ...prev, meeting_weekday: currentWeekdays.filter(day => day !== weekday) };
      } else {
        return { ...prev, meeting_weekday: [...currentWeekdays, weekday] };
      }
    });
  };

  const handleCategoryToggle = (category: string) => {
    setMeeting(prev => {
      const currentCategories = prev.meeting_category;
      if (currentCategories.includes(category)) {
        return { ...prev, meeting_category: currentCategories.filter(c => c !== category) };
      } else {
        return { ...prev, meeting_category: [...currentCategories, category] };
      }
    });
  };

  const handlePeriodSelect = (period: string) => {
    setMeeting(prev => ({ ...prev, meeting_period: period }));
  };

  const handlePlatformToggle = (platform: string) => {
    setMeeting(prev => {
      const currentPlatforms = prev.meeting_place;
      if (currentPlatforms.includes(platform)) {
        return { ...prev, meeting_place: currentPlatforms.filter(p => p !== platform) };
      } else {
        return { ...prev, meeting_place: [...currentPlatforms, platform] };
      }
    });
  };

  const handleSave = async () => {
    if (!meeting.meeting_name.trim() || !meeting.meeting_link.trim() || !meeting.meeting_time.trim() || meeting.meeting_weekday.length === 0 || meeting.meeting_category.length === 0 || meeting.meeting_place.length === 0) {
      Alert.alert(t('common.alert.errorTitle'), t('meetings.alerts.fillRequired'));
      return;
    }

    try {
      setLoading(true);
      
      let result;
      if (isEditMode && editMeetingId) {
        const meetingData = {
          meeting_name: meeting.meeting_name.trim(),
          meeting_category: meeting.meeting_category[0],
          meeting_link: meeting.meeting_link.trim(),
          meeting_time: meeting.meeting_time.trim(),
          meeting_weekday: meeting.meeting_weekday.join(', '),
          meeting_place: meeting.meeting_place[0],
          meeting_period: meeting.meeting_period,
          ...(meeting.group_id && { group_id: meeting.group_id }),
          ...(meeting.meeting_region.trim() && { meeting_region: meeting.meeting_region.trim() }),
          ...(meeting.meeting_password.trim() && { meeting_password: meeting.meeting_password.trim() }),
          ...(meeting.description.trim() && { description: meeting.description.trim() }),
          ...(meeting.header_image_url.trim() && { header_image_url: meeting.header_image_url.trim() }),
        };
        result = await MeetingsService.updateMeeting(editMeetingId, meetingData);
      } else {
        const meetingData = {
          meeting_name: meeting.meeting_name.trim(),
          meeting_category: meeting.meeting_category[0],
          meeting_link: meeting.meeting_link.trim(),
          meeting_time: meeting.meeting_time.trim(),
          meeting_weekday: meeting.meeting_weekday.join(', '),
          meeting_place: meeting.meeting_place[0],
          meeting_period: meeting.meeting_period,
          ...(meeting.group_id && { group_id: meeting.group_id }),
          ...(meeting.meeting_region.trim() && { meeting_region: meeting.meeting_region.trim() }),
          ...(meeting.meeting_password.trim() && { meeting_password: meeting.meeting_password.trim() }),
          ...(meeting.description.trim() && { description: meeting.description.trim() }),
          ...(meeting.header_image_url.trim() && { header_image_url: meeting.header_image_url.trim() }),
        };
        result = await MeetingsService.createMeeting(meetingData);
      }

      if (result.success) {
        Alert.alert(t('common.alert.successTitle'), isEditMode ? t('meetings.alerts.updateSuccess') : t('meetings.alerts.saveSuccess'));
        router.back();
      } else {
        Alert.alert(t('common.alert.errorTitle'), result.error || t('meetings.alerts.saveError'));
      }
    } catch (error) {
      console.error('Error saving meeting:', error);
      Alert.alert(t('common.alert.errorTitle'), t('meetings.alerts.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const renderWeekdayChip = (weekday: { value: string, label: string }) => (
    <SelectableChip
      key={weekday.value}
      label={weekday.label}
      isSelected={meeting.meeting_weekday.includes(weekday.value)}
      onPress={() => handleWeekdayToggle(weekday.value)}
    />
  );

  const getWeekdayOptions = () => [
    { value: 'monday', label: t('common.weekdays.long.monday') },
    { value: 'tuesday', label: t('common.weekdays.long.tuesday') },
    { value: 'wednesday', label: t('common.weekdays.long.wednesday') },
    { value: 'thursday', label: t('common.weekdays.long.thursday') },
    { value: 'friday', label: t('common.weekdays.long.friday') },
    { value: 'saturday', label: t('common.weekdays.long.saturday') },
    { value: 'sunday', label: t('common.weekdays.long.sunday') },
  ];

  const getCategoryOptions = () => [
    { value: 'supportGroup', label: t('common.categories.supportGroup') },
    { value: 'workshop', label: t('common.categories.workshop') },
    { value: 'meditation', label: t('common.categories.meditation') },
    { value: 'therapy', label: t('common.categories.therapy') },
    { value: 'sharing', label: t('common.categories.sharing') },
  ];

  const getPlatformOptions = () => [
    { value: 'zoom', label: 'Zoom' },
    { value: 'google-meet', label: 'Google Meet' },
    { value: 'microsoft-teams', label: 'Microsoft Teams' },
    { value: 'jitsi-meet', label: 'Jitsi Meet' },
    { value: 'discord', label: 'Discord' },
    { value: 'in-person', label: t('meetings.form.fields.platform.inPerson') },
  ];

  const getPeriodOptions = () => [
    { value: 'morning', label: t('common.periods.morning') },
    { value: 'afternoon', label: t('common.periods.afternoon') },
    { value: 'evening', label: t('common.periods.evening') },
  ];

  const renderCategoryChip = (category: { value: string, label: string }) => (
    <SelectableChip
      key={category.value}
      label={category.label}
      isSelected={meeting.meeting_category.includes(category.value)}
      onPress={() => handleCategoryToggle(category.value)}
    />
  );

  const renderPlatformChip = (platform: { value: string, label: string }) => (
    <SelectableChip
      key={platform.value}
      label={platform.label}
      isSelected={meeting.meeting_place.includes(platform.value)}
      onPress={() => handlePlatformToggle(platform.value)}
    />
  );

  const renderGroupRadio = (group: MeetingGroup) => {
    const isSelected = meeting.group_id === group.id;
    
    return (
      <TouchableOpacity
        key={group.id}
        style={[
          styles.periodCard,
          isSelected && styles.selectedPeriodCard
        ]}
        onPress={() => setMeeting(prev => ({ ...prev, group_id: group.id }))}
      >
        <Text style={styles.periodOptionText}>
          {group.name}
        </Text>
        <View style={[
          styles.radioButton,
          isSelected && styles.selectedRadio
        ]}>
          {isSelected && <View style={styles.radioInner} />}
        </View>
      </TouchableOpacity>
    );
  };

  const renderPeriodRadio = (period: { value: string, label: string }) => {
    const isSelected = meeting.meeting_period === period.value;
    
    return (
      <TouchableOpacity
        key={period.value}
        style={[
          styles.periodCard,
          isSelected && styles.selectedPeriodCard
        ]}
        onPress={() => handlePeriodSelect(period.value)}
      >
        <Text style={styles.periodOptionText}>
          {period.label}
        </Text>
        <View style={[
          styles.radioButton,
          isSelected && styles.selectedRadio
        ]}>
          {isSelected && <View style={styles.radioInner} />}
        </View>
      </TouchableOpacity>
    );
  };

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <PageHeader title={isEditMode ? t('meetings.form.title.edit') : t('meetings.form.title.new')} onBack={() => router.back()} />
        <View style={styles.unauthorizedContainer}>
          <Text style={styles.unauthorizedText}>{t('meetings.form.messages.unauthorized')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader title={isEditMode ? t('meetings.form.title.edit') : t('meetings.form.title.new')} onBack={() => router.back()} />
      
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.name.label')}</Text>
          <TextInput
            style={styles.input}
            value={meeting.meeting_name}
            onChangeText={(text) => setMeeting(prev => ({ ...prev, meeting_name: text }))}
            placeholder={t('meetings.form.fields.name.placeholder')}
            placeholderTextColor="#A19F97"
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.link.label')}</Text>
          <TextInput
            style={styles.input}
            value={meeting.meeting_link}
            onChangeText={(text) => setMeeting(prev => ({ ...prev, meeting_link: text }))}
            placeholder={t('meetings.form.fields.link.placeholder')}
            placeholderTextColor="#A19F97"
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.time.label')}</Text>
          <TextInput
            style={styles.input}
            value={meeting.meeting_time}
            onChangeText={(text) => setMeeting(prev => ({ ...prev, meeting_time: text }))}
            placeholder={t('meetings.form.fields.time.placeholder')}
            placeholderTextColor="#A19F97"
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.category.label')}</Text>
          <View style={styles.chipsContainer}>
            {getCategoryOptions().map(renderCategoryChip)}
          </View>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.group.label')}</Text>
          <View style={styles.periodOptionsContainer}>
            {groups.length > 0 ? (
              groups.map(renderGroupRadio)
            ) : (
              <Text style={styles.helperText}>{t('meetings.form.fields.group.noGroups')}</Text>
            )}
          </View>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.weekdays.label')}</Text>
          <View style={styles.chipsContainer}>
            {getWeekdayOptions().map(renderWeekdayChip)}
          </View>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.platform.label')}</Text>
          <View style={styles.chipsContainer}>
            {getPlatformOptions().map(renderPlatformChip)}
          </View>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.period.label')}</Text>
          <View style={styles.periodOptionsContainer}>
            {getPeriodOptions().map(renderPeriodRadio)}
          </View>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.region.label')}</Text>
          <TextInput
            style={styles.input}
            value={meeting.meeting_region}
            onChangeText={(text) => setMeeting(prev => ({ ...prev, meeting_region: text }))}
            placeholder={t('meetings.form.fields.region.placeholder')}
            placeholderTextColor="#A19F97"
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.password.label')}</Text>
          <TextInput
            style={styles.input}
            value={meeting.meeting_password}
            onChangeText={(text) => setMeeting(prev => ({ ...prev, meeting_password: text }))}
            placeholder={t('meetings.form.fields.password.placeholder')}
            placeholderTextColor="#A19F97"
            secureTextEntry
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.description.label')}</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={meeting.description}
            onChangeText={(text) => setMeeting(prev => ({ ...prev, description: text }))}
            placeholder={t('meetings.form.fields.description.placeholder')}
            placeholderTextColor="#A19F97"
            multiline
            numberOfLines={4}
          />
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>{t('meetings.form.fields.headerImage.label')}</Text>
          <TextInput
            style={styles.input}
            value={meeting.header_image_url}
            onChangeText={(text) => setMeeting(prev => ({ ...prev, header_image_url: text }))}
            placeholder={t('meetings.form.fields.headerImage.placeholder')}
            placeholderTextColor="#A19F97"
            autoCapitalize="none"
            autoCorrect={false}
          />
          <Text style={styles.helperText}>
            {t('meetings.form.fields.headerImage.helperText')}
          </Text>
        </View>

        <View style={{ height: 120 }} />
      </ScrollView>

      <View style={styles.buttonContainer}>
        <View style={styles.buttonRow}>
          <SecondaryButton 
            title={t('common.cancel')} 
            onPress={() => router.back()}
            style={styles.cancelButton}
          />
          <PrimaryButton 
            title={isEditMode ? t('meetings.form.actions.saveChanges') : t('meetings.form.actions.create')} 
            onPress={handleSave}
            disabled={loading}
            style={styles.saveButton}
          />
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  field: {
    marginBottom: 24,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14,
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    borderWidth: 1,
    borderColor: '#E7EAED',
    minHeight: 48,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  helperText: {
    fontSize: 12,
    fontFamily: 'Inter_400Regular',
    color: '#7E7D75',
    marginTop: 4,
  },
  chipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  periodOptionsContainer: {
    gap: 12,
    marginTop: 4,
  },
  periodCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E7EAED',
    padding: 16,
  },
  selectedPeriodCard: {
    backgroundColor: '#E6EBF0',
    borderColor: '#015480',
  },
  periodOptionText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#151515',
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: '#E7EAED',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedRadio: {
    borderColor: '#015480',
    backgroundColor: '#E6EBF0',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#015480',
  },
  buttonContainer: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1,
  },
  unauthorizedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unauthorizedText: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
  },
});
